// One Lucide glyph per appointment concept, so the calendar's row menu, the series list and the status
// summary draw the same meaning with the same glyph (icon pass, 15 Oct: Lucide everywhere, the glyph
// Practice uses for the concept). The location-type marks (Office / Home / Virtual / Block) are Lucide too,
// the set Practice's calendar filter draws (Home, Video, Ban for a block), with a map pin for the clinic: a plus reads
// as "add" (Antonin 31 Oct, rule R-CMP-10).
import {
  Ban,
  Calendar,
  CalendarClock,
  CalendarPlus,
  CalendarX2,
  Check,
  Eye,
  Flag,
  Home,
  LogIn,
  MapPin,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Receipt,
  Repeat,
  Stethoscope,
  Undo2,
  UserCheck,
  UserX,
  Video,
  type LucideIcon,
} from 'lucide-react';
import type { AppointmentActionId } from '@/scheduling/lib/appointment-actions';
import { categoryColor } from '@/shared/lib/category-color';

export const APPT_GLYPH = {
  comment: MessageSquare,
  edit: Pencil,
  appointment: Calendar,
  preview: Eye,
  billing: Receipt,
  recurring: Repeat,
  cancelled: CalendarX2,
  noShow: UserX,
  check: Check,
  reschedule: CalendarClock,
  followUp: CalendarPlus,
  flag: Flag,
} satisfies Record<string, LucideIcon>;

export type ApptGlyph = keyof typeof APPT_GLYPH;

/** The location type's glyph: the one mark for an appointment's place, in every filter, tile and row. */
export const LOCATION_GLYPH = {
  office: MapPin,
  home: Home,
  virtual: Video,
  block: Ban,
} satisfies Record<string, LucideIcon>;

export type LocationType = keyof typeof LOCATION_GLYPH;

/** A location name ("Office", "Home visit", "Virtual") to its glyph; `fallback` for a name that is none of them. */
export function locationGlyph(name: string | null | undefined, fallback: LocationType = 'office'): LucideIcon {
  const n = (name ?? '').toLowerCase();
  if (n in LOCATION_GLYPH) return LOCATION_GLYPH[n as LocationType];
  if (n.includes('home')) return LOCATION_GLYPH.home;
  if (n.includes('virtual')) return LOCATION_GLYPH.virtual;
  return LOCATION_GLYPH[fallback];
}

/** A location name's category hue (in clinic jade, home orange, virtual sky), the calendar's and Practice's map. */
function locationHue(name: string | null | undefined): 'jade' | 'orange' | 'sky' {
  const n = (name ?? '').toLowerCase();
  if (n.includes('virtual') || n.includes('video') || n.includes('tele')) return 'sky';
  if (n.includes('home')) return 'orange';
  return 'jade';
}

/**
 * An appointment row's slim leading bar (Practice's rule, 15 Oct: the location mark stays neutral, "the visit type's
 * colour already shows as the row's slim leading bar"): the profile's picked colour on the muted Okabe-Ito ramp at
 * 500 (`categoryColor`, Flutter's "0xAARRGGBB" read too), else the location's hue at 500. Never the raw saturated
 * colour the clinic picked.
 */
export function appointmentEdgeColor(profileColor: string | null | undefined, locationName?: string | null): string {
  const picked = profileColor ? profileColor.trim().replace(/^0x/i, '#') : null;
  return categoryColor(picked, 500, `var(--cat-${locationHue(locationName)}-500)`);
}

/** One Lucide line icon per appointment row action (every id, so no action falls back to the Scrips font): the
 *  calendar row's icon bar and the patient record's appointment rows draw the same action with the same glyph. */
export const ROW_ACTION_GLYPH: Record<AppointmentActionId, LucideIcon> = {
  'mark-arrived': UserCheck,
  confirm: Check,
  'mark-checked-in': LogIn,
  'mark-no-show': UserX,
  'unmark-arrived': Undo2,
  'unmark-checked-in': Undo2,
  'unmark-no-show': Undo2,
  reschedule: CalendarClock,
  cancel: CalendarX2,
  edit: Pencil,
  'follow-up': CalendarPlus,
  overflow: MoreHorizontal,
  'add-note': MessageSquare,
  'open-billing-tab': Receipt,
  'view-recurring-series': Repeat,
  'view-pvs': Eye,
  'open-encounter': Stethoscope,
};
